import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { lib, AES, enc, SHA256 } from 'crypto-js';

export interface KeyRotationConfig {
  rotationIntervalMs: number;
  maxKeyAge: number;
  keyVersions: number;
}

export interface SecurityKey {
  id: string;
  key: string;
  version: number;
  createdAt: Date;
  expiresAt: Date;
  isActive: boolean;
}

export interface KeyManagementAudit {
  timestamp: Date;
  action: 'KEY_GENERATED' | 'KEY_ROTATED' | 'KEY_EXPIRED' | 'KEY_ACCESSED';
  keyId: string;
  userId?: string;
  deviceFingerprint?: string;
  success: boolean;
  details?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SecureKeyManagerService {
  private readonly STORAGE_PREFIX = 'secure_key_';
  private readonly MASTER_KEY_ID = 'master_encryption_key';
  private readonly KEY_ROTATION_CONFIG: KeyRotationConfig = {
    rotationIntervalMs: (environment.keyRotationIntervalHours || 24) * 60 * 60 * 1000, // Convert hours to ms
    maxKeyAge: 7 * 24 * 60 * 60 * 1000, // 7 days
    keyVersions: 3 // Keep 3 versions
  };

  private currentMasterKey$ = new BehaviorSubject<SecurityKey | null>(null);
  private keyRotationTimer: any;
  private auditLog: KeyManagementAudit[] = [];
  private isInitializing = false;
  private isInitialized = false;
  private readonly INIT_FLAG_KEY = 'secure_key_manager_initialized';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (this.isBrowser() && !this.isInitialized && !this.isInitializing) {
      // Check if already initialized in this session (prevents duplicate initialization in SSR)
      if (sessionStorage.getItem(this.INIT_FLAG_KEY) === 'true') {
        this.isInitialized = true;
        // Load existing key if available
        const existingKey = this.loadKeyFromStorage(this.MASTER_KEY_ID);
        if (existingKey && !this.isKeyExpired(existingKey)) {
          this.currentMasterKey$.next(existingKey);
        }
        return;
      }
      
      this.isInitializing = true;
      this.initializeKeyManagement().catch(error => {
        console.error('Failed to initialize key management:', error);
        this.isInitializing = false;
      });
    }
  }

  /**
   * Check if running in browser environment
   */
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Initialize the key management system
   */
  private async initializeKeyManagement(): Promise<void> {
    // Prevent duplicate initialization
    if (this.isInitialized || (this.isBrowser() && sessionStorage.getItem(this.INIT_FLAG_KEY) === 'true')) {
      this.isInitialized = true;
      this.isInitializing = false;
      return;
    }

    try {
      // Load or generate master key
      await this.loadOrGenerateMasterKey();
      
      // Start key rotation timer
      this.startKeyRotationTimer();
      
      // Clean up expired keys
      this.cleanupExpiredKeys();
      
      // Mark as initialized in sessionStorage to prevent duplicate initialization
      if (this.isBrowser()) {
        sessionStorage.setItem(this.INIT_FLAG_KEY, 'true');
      }
      
      this.logAuditEvent('KEY_GENERATED', this.currentMasterKey$.value?.id || '', true, 'Key management initialized');
      
      this.isInitialized = true;
      this.isInitializing = false;
    } catch (error) {
      console.error('Failed to initialize key management:', error);
      this.logAuditEvent('KEY_GENERATED', '', false, `Initialization failed: ${error}`);
      this.isInitializing = false;
    }
  }

  /**
   * Load existing master key or generate a new one
   */
  private async loadOrGenerateMasterKey(): Promise<void> {
    const existingKey = this.loadKeyFromStorage(this.MASTER_KEY_ID);
    
    if (existingKey && !this.isKeyExpired(existingKey)) {
      this.currentMasterKey$.next(existingKey);
    } else {
      await this.generateNewMasterKey();
    }
  }

  /**
   * Generate a new master encryption key
   */
  private async generateNewMasterKey(): Promise<SecurityKey> {
    const keyId = this.generateKeyId();
    const keyData = this.generateSecureKey(256); // 256-bit key
    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.KEY_ROTATION_CONFIG.maxKeyAge);

    const newKey: SecurityKey = {
      id: keyId,
      key: keyData,
      version: this.getNextKeyVersion(),
      createdAt: now,
      expiresAt: expiresAt,
      isActive: true
    };

    // Store the new key
    this.storeKeySecurely(this.MASTER_KEY_ID, newKey);
    this.currentMasterKey$.next(newKey);

    this.logAuditEvent('KEY_GENERATED', keyId, true, 'New master key generated');
    return newKey;
  }

  /**
   * Rotate the master key
   */
  public async rotateMasterKey(): Promise<SecurityKey> {
    try {
      const oldKey = this.currentMasterKey$.value;
      const newKey = await this.generateNewMasterKey();

      // Archive old key for decryption purposes
      if (oldKey) {
        oldKey.isActive = false;
        this.storeKeySecurely(`${this.MASTER_KEY_ID}_v${oldKey.version}`, oldKey);
      }

      this.logAuditEvent('KEY_ROTATED', newKey.id, true, 'Master key rotated successfully');
      return newKey;
    } catch (error) {
      this.logAuditEvent('KEY_ROTATED', '', false, `Key rotation failed: ${error}`);
      throw error;
    }
  }

  /**
   * Get the current active master key
   */
  public getCurrentMasterKey(): Observable<SecurityKey | null> {
    return this.currentMasterKey$.asObservable();
  }

  /**
   * Get the current master key synchronously
   */
  public getCurrentMasterKeySync(): SecurityKey | null {
    return this.currentMasterKey$.value;
  }

  /**
   * Generate a secure key for encryption
   */
  public generateSecureKey(bitLength: number = 256): string {
    const bytes = bitLength / 8;
    return lib.WordArray.random(bytes).toString();
  }

  /**
   * Generate a unique key ID
   */
  private generateKeyId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    return `key_${timestamp}_${random}`;
  }

  /**
   * Get the next key version number
   */
  private getNextKeyVersion(): number {
    const currentKey = this.currentMasterKey$.value;
    return currentKey ? currentKey.version + 1 : 1;
  }

  /**
   * Store a key securely in session storage
   */
  private storeKeySecurely(keyId: string, key: SecurityKey): void {
    if (!this.isBrowser()) {
      console.warn('Cannot store keys in SSR environment');
      return;
    }
    
    try {
      const encryptedKey = this.encryptKeyForStorage(key);
      sessionStorage.setItem(`${this.STORAGE_PREFIX}${keyId}`, encryptedKey);
    } catch (error) {
      console.error('Failed to store key securely:', error);
      throw new Error('Key storage failed');
    }
  }

  /**
   * Load a key from secure storage
   */
  private loadKeyFromStorage(keyId: string): SecurityKey | null {
    if (!this.isBrowser()) {
      console.warn('Cannot load keys in SSR environment');
      return null;
    }
    
    try {
      const encryptedKey = sessionStorage.getItem(`${this.STORAGE_PREFIX}${keyId}`);
      if (!encryptedKey) return null;

      return this.decryptKeyFromStorage(encryptedKey);
    } catch (error) {
      console.error('Failed to load key from storage:', error);
      return null;
    }
  }

  /**
   * Encrypt key for storage (using device-specific entropy)
   */
  private encryptKeyForStorage(key: SecurityKey): string {
    const deviceKey = this.getDeviceSpecificKey();
    const keyData = JSON.stringify(key);
    return AES.encrypt(keyData, deviceKey).toString();
  }

  /**
   * Decrypt key from storage
   */
  private decryptKeyFromStorage(encryptedKey: string): SecurityKey {
    const deviceKey = this.getDeviceSpecificKey();
    const decryptedBytes = AES.decrypt(encryptedKey, deviceKey);
    const decryptedData = decryptedBytes.toString(enc.Utf8);
    
    // Check if decrypted data is valid before parsing
    if (!decryptedData || decryptedData === 'undefined' || decryptedData.trim() === '') {
      throw new Error('Decrypted data is empty or undefined');
    }
    
    let parsedKey;
    try {
      parsedKey = JSON.parse(decryptedData);
    } catch (error) {
      throw new Error(`Failed to parse decrypted key data: ${error}`);
    }
    
    // Validate parsed key structure
    if (!parsedKey || typeof parsedKey !== 'object') {
      throw new Error('Parsed key data is not a valid object');
    }
    
    // Convert date strings back to Date objects
    return {
      ...parsedKey,
      createdAt: new Date(parsedKey.createdAt),
      expiresAt: new Date(parsedKey.expiresAt)
    };
  }

  /**
   * Generate device-specific key for local encryption
   */
  private getDeviceSpecificKey(): string {
    if (!this.isBrowser()) {
      // Fallback for SSR environment
      return SHA256('ssr-fallback-key').toString();
    }
    
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const platform = navigator.platform;
    const deviceInfo = `${userAgent}${language}${platform}`;
    return SHA256(deviceInfo).toString();
  }

  /**
   * Check if a key is expired
   */
  private isKeyExpired(key: SecurityKey): boolean {
    return new Date() > key.expiresAt;
  }

  /**
   * Start the automatic key rotation timer
   */
  private startKeyRotationTimer(): void {
    this.keyRotationTimer = interval(this.KEY_ROTATION_CONFIG.rotationIntervalMs).subscribe(() => {
      this.rotateMasterKey().catch(error => {
        console.error('Automatic key rotation failed:', error);
      });
    });
  }

  /**
   * Stop the key rotation timer
   */
  public stopKeyRotation(): void {
    if (this.keyRotationTimer) {
      this.keyRotationTimer.unsubscribe();
      this.keyRotationTimer = null;
    }
  }

  /**
   * Clean up expired keys from storage
   */
  private cleanupExpiredKeys(): void {
    if (!this.isBrowser()) {
      console.warn('Cannot cleanup keys in SSR environment');
      return;
    }
    
    try {
      const keys = Object.keys(sessionStorage).filter(key => key.startsWith(this.STORAGE_PREFIX));
      
      keys.forEach(keyName => {
        const key = this.loadKeyFromStorage(keyName.replace(this.STORAGE_PREFIX, ''));
        if (key && this.isKeyExpired(key)) {
          sessionStorage.removeItem(keyName);
          this.logAuditEvent('KEY_EXPIRED', key.id, true, 'Expired key cleaned up');
        }
      });
    } catch (error) {
      console.error('Failed to cleanup expired keys:', error);
    }
  }

  /**
   * Get key management statistics
   */
  public getKeyManagementStats(): any {
    const currentKey = this.currentMasterKey$.value;
    return {
      currentKeyId: currentKey?.id,
      currentKeyVersion: currentKey?.version,
      keyAge: currentKey ? Date.now() - currentKey.createdAt.getTime() : 0,
      timeUntilRotation: currentKey ? currentKey.expiresAt.getTime() - Date.now() : 0,
      auditLogSize: this.auditLog.length
    };
  }

  /**
   * Log audit events
   */
  private logAuditEvent(
    action: KeyManagementAudit['action'],
    keyId: string,
    success: boolean,
    details?: string
  ): void {
    const auditEvent: KeyManagementAudit = {
      timestamp: new Date(),
      action,
      keyId,
      success,
      details
    };

    this.auditLog.push(auditEvent);
    
    // Keep only last 100 audit events in memory
    if (this.auditLog.length > 100) {
      this.auditLog = this.auditLog.slice(-100);
    }

    // Send audit log to backend for compliance
    this.sendAuditLogToBackend(auditEvent);
  }

  /**
   * Send audit log to backend (deferred, non-blocking).
   * Audit events are batched and sent lazily to avoid adding network overhead
   * during the login critical path.
   */
  private sendAuditLogToBackend(auditEvent: KeyManagementAudit): void {
    // Fire-and-forget after a short delay to avoid blocking the caller
    setTimeout(() => {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Security-Context': 'key-management-audit'
      });

      const apiUrl = environment.api.endsWith('/') ? environment.api.slice(0, -1) : environment.api;
      
      this.http.post(`${apiUrl}/security/audit/key-management`, auditEvent, { headers })
        .subscribe({
          next: () => {},
          error: () => {
            // Silently ignore — audit logging should never block the user
          }
        });
    }, 2000);
  }

  /**
   * Get audit log for compliance reporting
   */
  public getAuditLog(): KeyManagementAudit[] {
    return [...this.auditLog];
  }

  /**
   * Force key rotation (for testing or emergency purposes)
   */
  public async forceKeyRotation(): Promise<SecurityKey> {
    return await this.rotateMasterKey();
  }

  /**
   * Validate key integrity
   */
  public validateKeyIntegrity(): boolean {
    try {
      const currentKey = this.currentMasterKey$.value;
      if (!currentKey) return false;

      // Perform basic validation
      return !!(
        currentKey.id &&
        currentKey.key &&
        currentKey.version > 0 &&
        currentKey.createdAt &&
        currentKey.expiresAt &&
        !this.isKeyExpired(currentKey)
      );
    } catch (error) {
      console.error('Key integrity validation failed:', error);
      return false;
    }
  }

  /**
   * Cleanup on service destruction
   */
  public ngOnDestroy(): void {
    this.stopKeyRotation();
  }

  /**
   * Clear all keys from storage and memory
   */
  public clearAllKeys(): void {
    if (!this.isBrowser()) {
      console.warn('Cannot clear keys in SSR environment');
      return;
    }
    
    try {
      // Clear current key from memory
      this.currentMasterKey$.next(null);
      
      // Clear all keys from session storage
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // Log audit event
      this.logAuditEvent('KEY_EXPIRED', 'all_keys', true, 'All keys cleared from storage');
      
      // All encryption keys cleared successfully
    } catch (error) {
      console.error('Failed to clear all keys:', error);
      this.logAuditEvent('KEY_EXPIRED', 'all_keys', false, `Clear keys failed: ${error}`);
    }
  }
}